import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useContext, useEffect, useRef } from 'react';
import { $getRoot, $isElementNode, LexicalNode } from 'lexical';
import {
  $isProtectedParagraphNode,
  ProtectedParagraphNode,
} from '../nodes/Localizable/ProtectedParagraphNode.ts';
import { $isLocalizableTextNode } from '../nodes/Localizable/LocalizableTextNode.tsx';
import { PageDesignerContext } from '../../DesignerNewPage.tsx';
import { $isListItemNode, $isListNode, ListItemNode } from '@lexical/list';
import { $isHeadingNode, HeadingNode } from '@lexical/rich-text';
import {
  $isTabContainerNode,
  TabContainerNode,
} from '../nodes/TabContainerNode.ts';

export type SupportedNode =
  | ProtectedParagraphNode
  | ListItemNode
  | HeadingNode
  | TabContainerNode;
type Block = SupportedNode[];

export function CustomTranslateIconPlugin() {
  const [editor] = useLexicalComposerContext();
  const pageDesignerContext = useContext(PageDesignerContext);
  const highlightRef = useRef<Record<string, Set<string>>>({});

  useEffect(() => {
    const shouldShowIconForNode = (node: LexicalNode): boolean => {
      let hasMatch = false;

      if (!highlightRef.current[node.getKey()]) {
        highlightRef.current[node.getKey()] = new Set<string>();
      }

      if ($isElementNode(node)) {
        const children: LexicalNode[] = node.getChildren();

        const validChildKeys = new Set<string>();

        for (const child of children) {
          if ($isLocalizableTextNode(child)) {
            const {
              __languagesLastManuallyChanged,
              __originalLanguage,
              __languagesTranslatedByAI,
            } = child;

            const currentLanguage = pageDesignerContext!.pageEditorLanguage!;
            const childKey = child.getKey();

            if (!__originalLanguage) {
              continue;
            }

            const anyLanguageRegex =
              /\p{Script=Latin}|\p{Script=Cyrillic}|\p{Script=Arabic}|\p{Script=Hebrew}|\p{Script=Han}|\p{Script=Hiragana}|\p{Script=Katakana}|\p{Script=Devanagari}|\p{Script=Greek}|\p{Script=Hangul}|\p{Script=Thai}|\p{Script=Georgian}|\p{Script=Ethiopic}|\p{Script=Armenian}|\p{Script=Tamil}|\p{Script=Runic}|\p{Script=Glagolitic}/u;

            const text = child.__translations[__originalLanguage];

            if (
              !text ||
              !anyLanguageRegex.test(text) ||
              text.startsWith('\n')
            ) {
              continue;
            }

            const currentLangIndex =
              __languagesLastManuallyChanged.indexOf(currentLanguage);
            const originalLangIndex =
              __languagesLastManuallyChanged.indexOf(__originalLanguage);
            const currentLangAIIndex =
              __languagesTranslatedByAI.indexOf(currentLanguage);

            const shouldBeTranslated =
              (currentLangIndex === -1 ||
                currentLangIndex < originalLangIndex) &&
              currentLangAIIndex === -1;

            if (shouldBeTranslated) {
              validChildKeys.add(childKey);
              hasMatch = true;
            }
          } else {
            if (shouldShowIconForNode(child)) {
              hasMatch = true;
              const childHighlights = highlightRef.current[child.getKey()];
              if (childHighlights && childHighlights.size > 0) {
                for (const key of childHighlights) {
                  validChildKeys.add(key);
                }
                delete highlightRef.current[child.getKey()];
              }
            }
          }
        }

        const currentSet = highlightRef.current[node.getKey()];
        for (const key of validChildKeys) {
          currentSet.add(key);
        }
        for (const key of currentSet) {
          if (!validChildKeys.has(key)) {
            currentSet.delete(key);
          }
        }
      }

      if (highlightRef.current[node.getKey()].size === 0) {
        delete highlightRef.current[node.getKey()];
      }

      return hasMatch;
    };

    const findBlockBoundaries = (nodes: LexicalNode[]): Block[] => {
      const blocks: Block[] = [];
      let currentBlock: SupportedNode[] = [];
      let inUntranslatedBlock = false;
      let currentTypeOfNode = '';

      const isDefaultLanguage =
        !pageDesignerContext?.pageEditorLanguage ||
        pageDesignerContext?.pageEditorLanguage ===
          pageDesignerContext.defaultLanguage;

      if (isDefaultLanguage) return blocks;

      const processNode = (node: SupportedNode, shouldShowIcon: boolean) => {
        const nodeType = node.getType();

        if (shouldShowIcon && !inUntranslatedBlock) {
          inUntranslatedBlock = true;
          currentBlock = [node];
          currentTypeOfNode = nodeType;
        } else if (shouldShowIcon && inUntranslatedBlock) {
          if (nodeType === currentTypeOfNode) {
            currentBlock.push(node);
          } else {
            blocks.push([...currentBlock]);
            currentBlock = [node];
            currentTypeOfNode = nodeType;
          }
        } else if (!shouldShowIcon && inUntranslatedBlock) {
          blocks.push([...currentBlock]);
          currentBlock = [];
          inUntranslatedBlock = false;
        }
      };

      for (const node of nodes) {
        if (
          $isProtectedParagraphNode(node) ||
          $isHeadingNode(node) ||
          $isTabContainerNode(node)
        ) {
          processNode(node, shouldShowIconForNode(node));
        } else if ($isListNode(node)) {
          for (const child of node.getChildren()) {
            if ($isListItemNode(child)) {
              processNode(child, shouldShowIconForNode(child));
            }
          }
        }
      }

      if (currentBlock.length > 0) {
        blocks.push([...currentBlock]);
      }

      return blocks;
    };

    const updateIcons = (): void => {
      editor.getEditorState().read(() => {
        const root = $getRoot();
        const allNodes: LexicalNode[] = root.getChildren();
        const untranslatedBlocks: Block[] = findBlockBoundaries(allNodes);

        untranslatedBlocks.forEach((block: Block) => {
          if (block.length > 0) {
            const firstNode: SupportedNode = block[0];

            const firstElement: HTMLElement | null = editor.getElementByKey(
              firstNode.getKey(),
            );

            if (firstElement) {
              firstElement.classList.add('displayBlock');

              const parent = firstNode.getParent();

              if (
                $isListItemNode(firstNode) &&
                $isListNode(parent) &&
                parent.__listType === 'number'
              ) {
                firstElement.classList.add('marginTopMinus24');
              }

              const tooltipElement = firstElement.querySelector(
                '[class*="localizationTooltip"]',
              );

              if (tooltipElement) {
                const handleMouseEnter = () => {
                  highlightBlock(block);
                };

                const handleMouseLeave = () => {
                  highlightBlock(block, true);
                };

                tooltipElement.addEventListener('mouseenter', handleMouseEnter);
                tooltipElement.addEventListener('mouseleave', handleMouseLeave);

                (tooltipElement as any)._mouseEnterHandler = handleMouseEnter;
                (tooltipElement as any)._mouseLeaveHandler = handleMouseLeave;
              }
            }
          }
        });
      });
    };

    const highlightBlock = (
      blockNodes: SupportedNode[],
      isRemoving?: boolean,
    ) => {
      blockNodes.forEach((node: SupportedNode) => {
        const nodeKeysToHighlight = highlightRef.current[node.getKey()];

        for (const key of nodeKeysToHighlight) {
          const element: HTMLElement | null = editor.getElementByKey(key);

          if (element) {
            if (isRemoving) {
              element.classList.remove('translationBackground');
            } else {
              element.classList.add('translationBackground');
            }
          }
        }
      });
    };

    const cleanupEventListenersAndClasses = () => {
      const translationIcons: NodeListOf<HTMLElement> =
        document.querySelectorAll('.displayBlock');

      const highlightedElements: NodeListOf<HTMLElement> =
        document.querySelectorAll('.translationBackground');

      translationIcons.forEach((element) => {
        element.classList.remove('displayBlock');
        element.classList.remove('marginTopMinus24');
      });

      highlightedElements.forEach((element) => {
        element.classList.remove('translationBackground');
      });

      const localizationTooltips: NodeListOf<HTMLElement> =
        document.querySelectorAll('[class*="localizationTooltip"]');

      localizationTooltips.forEach((icon) => {
        if ((icon as any)._mouseEnterHandler) {
          icon.removeEventListener(
            'mouseenter',
            (icon as any)._mouseEnterHandler,
          );
        }
        if ((icon as any)._mouseLeaveHandler) {
          icon.removeEventListener(
            'mouseleave',
            (icon as any)._mouseLeaveHandler,
          );
        }
      });
    };

    if (!editor.isEditable()) {
      return;
    }

    const removeUpdateListener = editor.registerUpdateListener(() => {
      cleanupEventListenersAndClasses();
      updateIcons();
    });

    updateIcons();

    return () => {
      removeUpdateListener();
      cleanupEventListenersAndClasses();
    };
  }, [
    editor,
    pageDesignerContext?.pageEditorLanguage,
    pageDesignerContext?.defaultLanguage,
  ]);

  return null;
}
